/**
 * The aperture: cosine top-k over a quantized lattice.
 *
 * Brute force on purpose. At 4217 facts by 768 dims this is 3.2 million
 * multiply-adds, which lands well under a millisecond. An approximate index
 * would add a dependency, a build step, and a recall cliff, to save time we
 * do not need. If a pack ever reaches six figures of facts, revisit with a
 * measurement rather than a hunch.
 *
 * Pure and isomorphic so the headless bench scores the exact code the page
 * runs.
 */

import type { QuantizedVectors } from "./quant.ts";

export type Hit = { index: number; score: number };

/**
 * Top-k by cosine similarity. The query must already be L2-normalized; pack
 * vectors are normalized before quantization, so a dot product is the cosine.
 *
 * Scores use the quantized values directly rather than dequantizing into a
 * scratch buffer: the per-row scale is a positive constant, so it can be
 * folded in once at the end of each row.
 */
export function topK(q: QuantizedVectors, query: Float32Array, k: number, filter?: (i: number) => boolean): Hit[] {
  if (query.length !== q.dim) {
    throw new Error(`topK: query dim ${query.length} does not match pack dim ${q.dim}`);
  }
  if (k <= 0) return [];

  // A small insertion-sorted array beats a heap at these k values and keeps
  // the result deterministic, which matters for reproducible bench runs.
  const best: Hit[] = [];
  const worst = () => (best.length ? best[best.length - 1].score : -Infinity);

  const { data, scales, dim, count } = q;

  for (let r = 0; r < count; r++) {
    if (filter && !filter(r)) continue;
    const off = r * dim;
    let dot = 0;
    for (let i = 0; i < dim; i++) dot += data[off + i] * query[i];
    const score = dot * scales[r];

    if (best.length < k) {
      insert(best, { index: r, score });
    } else if (score > worst()) {
      best.pop();
      insert(best, { index: r, score });
    }
  }
  return best;
}

function insert(arr: Hit[], hit: Hit) {
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    // Descending by score. Ties break toward the lower index so results are
    // stable across runs.
    if (arr[mid].score > hit.score || (arr[mid].score === hit.score && arr[mid].index < hit.index)) lo = mid + 1;
    else hi = mid;
  }
  arr.splice(lo, 0, hit);
}

/**
 * recall@k: the fraction of queries whose known-correct fact appears in the
 * top k. This is the number that chooses the shipped embedder.
 */
export function recallAtK(hits: Hit[][], expected: number[], k: number): number {
  if (hits.length !== expected.length) throw new Error("recallAtK: length mismatch");
  if (hits.length === 0) throw new Error("recallAtK: empty denominator");
  let found = 0;
  for (let i = 0; i < hits.length; i++) {
    if (hits[i].slice(0, k).some((h) => h.index === expected[i])) found++;
  }
  return found / hits.length;
}

/**
 * Mean reciprocal rank. Reported alongside recall because two embedders can
 * tie on recall@5 while one consistently ranks the right fact first.
 */
export function mrr(hits: Hit[][], expected: number[]): number {
  if (hits.length === 0) throw new Error("mrr: empty denominator");
  let sum = 0;
  for (let i = 0; i < hits.length; i++) {
    const rank = hits[i].findIndex((h) => h.index === expected[i]);
    if (rank >= 0) sum += 1 / (rank + 1);
  }
  return sum / hits.length;
}
